const ISO_CURRENCY_CODE = /^[A-Z]{3}$/

const warnNonIso = (currency: string) => {
  if (process.env.NEXT_PUBLIC_NODE_ENV !== 'production') {
    console.warn(`formatCurrency: expected ISO 4217 code, got "${currency}"`)
  }
}

const getCurrencySymbol = (currency: string, locale: string): string =>
  new Intl.NumberFormat(locale, { currency, currencyDisplay: 'narrowSymbol', style: 'currency' })
    .formatToParts(1)
    .find((part) => part.type === 'currency')?.value ?? currency

export const formatCurrencyShort = (value: number, currency: string, locale = 'en-US'): string => {
  const isIso = ISO_CURRENCY_CODE.test(currency)
  if (!isIso) warnNonIso(currency)

  const symbol = isIso ? getCurrencySymbol(currency, locale) : currency
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)

  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}${symbol}${(abs / 1_000).toFixed(1)}K`

  return `${sign}${symbol}${abs}`
}

export const formatCurrency = (
  value: null | number | string | undefined,
  currency: string,
  locale = 'en-US',
  fallback = '--',
): string => {
  if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
    return fallback
  }

  const n = typeof value === 'string' ? Number(value.trim()) : value

  if (typeof n !== 'number' || !Number.isFinite(n)) return fallback

  if (!ISO_CURRENCY_CODE.test(currency)) {
    warnNonIso(currency)
    const formatted = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      signDisplay: 'never',
      style: 'decimal',
    }).format(Math.abs(n))

    return `${n < 0 ? '-' : ''}${currency}${formatted}`
  }

  return new Intl.NumberFormat(locale, {
    currency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(n)
}
